import {
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
} from '../problemCanvas/problemCanvasConstants';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { BasicObject, getBasicObject } from '../uiObjects/basicObject';

import {
  GAP_BETWEEN_COLUMNS,
  LABEL_HEIGHT,
  MARGIN,
} from './SectionedHelpConstants';
import { SectionLabel } from './SectionLabel';
import { StaticPanelHelpSection } from './StaticPanelHelpSection';
import type { StaticPanelHelpSectionDTO } from './StaticPanelHelpSection';
import { TwoColumnHelpSection } from './TwoColumnHelpSection';
import type { TwoColumnHelpSectionDTO } from './TwoColumnHelpSection';

const UNDER_LABEL_MARGIN = 8;
const GAP_BETWEEN_SECTIONS = 22;
const MAX_FIRST_COLUMN_WIDTH = PROBLEM_WIDTH - NARROW_PROBLEM_WIDTH;
const MAX_HELP_WIDTH = PROBLEM_WIDTH - 2 * MARGIN;

const DIVIDER_COLOR = 'blue';

interface SectionedHelpDTO {
  sections: HelpSectionDTO[];
}

type HelpSectionDTO = TwoColumnHelpSectionDTO | StaticPanelHelpSectionDTO;

export function getSectionedHelpPanel(
  problemJS: ProblemJSDependencies,
  dto: SectionedHelpDTO
): BasicObject {
  const ret = new BasicObject();

  ret.gmmName = 'sectionedHelp';

  // Count how many sections have answers
  const answerSectionCount = dto.sections.reduce(
    (count, section) => count + (sectionHasAnswer(section) ? 1 : 0),
    0
  );

  let answerIndex = 1;
  const sections = dto.sections.map(section => {
    let sec: TwoColumnHelpSection | StaticPanelHelpSection;
    const answerNumber = answerSectionCount > 1 ? answerIndex : undefined;
    const isAnswerSection = sectionHasAnswer(section);

    if (isTwoColumnHelpSection(section)) {
      sec = new TwoColumnHelpSection(
        problemJS,
        section,
        isAnswerSection ? answerNumber : undefined
      );
    } else {
      sec = new StaticPanelHelpSection(
        problemJS,
        section,
        MAX_HELP_WIDTH,
        isAnswerSection,
        answerNumber
      );
    }

    if (isAnswerSection) {
      answerIndex += 1;
    }

    return { section: sec, label: section.label };
  });

  // find max width of left column of two-column sections
  let leftColumnWidth = Math.max(
    0,
    ...sections
      .filter(sectionObj => sectionObj.section instanceof TwoColumnHelpSection)
      // 'as TwoColumnHelpSection' is needed to keep linter happy (despite filter above)
      .map(sectionObj =>
        (sectionObj.section as TwoColumnHelpSection).getLeftColumnWidth()
      )
  );

  // ensure left column does not exceed max
  leftColumnWidth = Math.min(leftColumnWidth, MAX_FIRST_COLUMN_WIDTH);

  const maxRightColumnWidth =
    MAX_HELP_WIDTH - leftColumnWidth - GAP_BETWEEN_COLUMNS;

  for (const sectionObj of sections) {
    if (sectionObj.section instanceof TwoColumnHelpSection) {
      sectionObj.section.buildRight(maxRightColumnWidth);
    }
  }

  const hasExplanations = sections.some(
    sectionObj =>
      sectionObj.section instanceof TwoColumnHelpSection &&
      sectionHasRightColumnContent(sectionObj.section)
  );
  const columnGap = hasExplanations ? GAP_BETWEEN_COLUMNS : 0;

  for (const sectionObj of sections) {
    if (sectionObj.section instanceof TwoColumnHelpSection) {
      sectionObj.section.layoutCells(leftColumnWidth, columnGap);
    }
  }

  const dividerWidth = Math.max(
    0,
    ...sections.map(obj => obj.section.viewportW)
  );

  let nextSectionY = 0;

  for (let i = 0; i < sections.length; i++) {
    const { section, label } = sections[i];

    section.viewportY = nextSectionY;

    // Render section label if present
    let labelHeight = 0;

    if (label) {
      const labelObj = new SectionLabel(label, section.viewportW, nextSectionY);

      ret.add(labelObj);
      section.viewportY += labelObj.viewportH;
      labelHeight = labelObj.viewportH;

      // Shift static panel down if label is present and not a TwoColumnHelpSection
      if (!(section instanceof TwoColumnHelpSection)) {
        section.viewportY += UNDER_LABEL_MARGIN;
        labelHeight += UNDER_LABEL_MARGIN;
      }
    }

    ret.add(section);
    // Add label height to section height for layout purposes
    nextSectionY += section.viewportH + labelHeight + GAP_BETWEEN_SECTIONS;

    if (i < sections.length - 1) {
      ret.add(
        getHorizontalDivider(
          nextSectionY - GAP_BETWEEN_SECTIONS / 2,
          dividerWidth
        )
      );
    }
  }

  const helpWidth = Math.max(0, ...sections.map(obj => obj.section.viewportW));
  const helpHeight = nextSectionY - GAP_BETWEEN_SECTIONS;

  ret.setAllDim(helpWidth, helpHeight);

  // Add vertical divider for each TwoColumnHelpSection with explanations
  for (let i = 0; i < sections.length; i++) {
    const { section, label } = sections[i];

    if (
      section instanceof TwoColumnHelpSection &&
      sectionHasRightColumnContent(section)
    ) {
      // Calculate total height including label if present
      const labelHeight = label ? LABEL_HEIGHT : 0;
      // Start divider half the margin above, end half the margin below
      const halfMargin = GAP_BETWEEN_SECTIONS / 2;
      let dividerY = section.viewportY - labelHeight - halfMargin;
      let dividerHeight =
        section.viewportH + labelHeight + GAP_BETWEEN_SECTIONS;

      // If first section, don't go above 0
      if (i === 0) {
        dividerY = 0;
        dividerHeight = section.viewportH + labelHeight + halfMargin;
      }

      // If last section, don't extend below
      if (i === sections.length - 1) {
        dividerHeight = section.viewportH + labelHeight + halfMargin;
      }

      const divider = getVerticalDivider(
        leftColumnWidth + GAP_BETWEEN_SECTIONS / 2,
        dividerHeight
      );

      divider.viewportY = dividerY;
      ret.add(divider);
    }
  }

  sections.forEach(sectionObj => {
    if (sectionObj.section instanceof TwoColumnHelpSection) {
      sectionObj.section.setRowsToWidth(helpWidth);
    }
  });

  return ret;
}

const sectionHasAnswer = (section: HelpSectionDTO): boolean =>
  isTwoColumnHelpSection(section)
    ? !section.sectionWithNoAnswer
    : StaticPanelHelpSection.hasAnswer(section);

const sectionHasRightColumnContent = (section: TwoColumnHelpSection): boolean =>
  section.rightColumnCells.some(cell => cell.viewportW > 0);

const isTwoColumnHelpSection = (
  section: HelpSectionDTO
): section is TwoColumnHelpSectionDTO =>
  'twoColumnHelpSteps' in section && Array.isArray(section.twoColumnHelpSteps);

const getHorizontalDivider = (y: number, width: number): BasicObject => {
  const divider = getBasicObject();

  divider.gmmName = 'help divider';
  divider.setAllDim(width, 1);
  divider.viewportY = y;
  divider.fill = DIVIDER_COLOR;

  return divider;
};

const getVerticalDivider = (x: number, height: number): BasicObject => {
  const divider = getBasicObject();

  divider.gmmName = 'help vertical divider';
  divider.setAllDim(1, height);
  divider.viewportX = x;
  divider.fill = DIVIDER_COLOR;

  return divider;
};
